import styled from 'styled-components';

import { generateFluidPxScale } from '@/components/blog-2/utils';
import { CustomerStories } from '@/components/home-2025/CustomerStories';
import {
  backgroundImageHatched,
  CheckeredMargin,
  ContentMarginLine,
  gridColorSelector
} from '@/components/v2/GridLines';
import {
  LayoutGridCondensed,
  layoutGridCondensedPadding,
  LayoutSubgrid
} from '@/components/v2/Page/LayoutGrid';
import { styles as textStyles } from '@/components/v2/UtilityCss/Text';
import { INTERNAL_LINKS } from '@/utils';

import { config } from '../config';
import { Workflow } from '../Workflow';

import { AiCell, MultiplayerCell, NoCodeCell, PolyglotCell } from './Cell';

const Section = styled(Workflow.Section)`
  padding: 0;
  border-bottom: 1px solid ${({ theme }) => gridColorSelector(theme, 'major')};
`;

const Bento = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${generateFluidPxScale(8, 14)};
  padding: ${generateFluidPxScale(8, 14)};
  background-image: ${({ theme }) => backgroundImageHatched(theme)};

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    grid-template-columns: 1fr;
  }
`;

const SubHeadline = styled(Workflow.SubHeadline)`
  max-width: 50rem;
  padding-right: ${layoutGridCondensedPadding};

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    padding: 0;
  }
`;

const StyledContentMarginLine = styled(ContentMarginLine)`
  --border-color: ${({ theme }) => theme.palette.fg600};
`;

const HatchedContentMarginLine = styled(StyledContentMarginLine)`
  background-image: ${({ theme }) => backgroundImageHatched(theme)};
`;

const HatchedCondensedGrid = styled(LayoutGridCondensed)`
  border-bottom: 1px solid ${({ theme }) => gridColorSelector(theme, 'minor')};

  &[data-bottom] {
    border-bottom: none;
    border-top: 1px solid ${({ theme }) => gridColorSelector(theme, 'minor')};
    height: 48px;
  }
`;

const HeadlineContainer = styled.div`
  padding: ${layoutGridCondensedPadding};
  display: grid;
  grid-template-columns: 50% 50%;
  gap: ${layoutGridCondensedPadding};
  align-items: flex-end;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    padding-top: 32px;
    padding-bottom: 32px;
    grid-template-columns: 1fr;
  }
`;

const Headline = styled(Workflow.Headline)`
  margin-bottom: 0;
`;

export function Notebooks() {
  return (
    <Section hideGridLines id={config.workflows.notebooks.anchor}>
      <CheckeredMargin />
      <LayoutSubgrid data-content-grid>
        <HatchedCondensedGrid>
          <HatchedContentMarginLine />
          <HeadlineContainer>
            <div>
              <Workflow.TagLink
                className={textStyles.annotation}
                data-notebooks
                to={INTERNAL_LINKS.Notebooks}>
                {config.workflows.notebooks.icon} Notebooks
              </Workflow.TagLink>
              <Headline>Fast, flexible, collaborative analytics</Headline>
            </div>
            <SubHeadline>
              <Workflow.Em>
                Hex's notebooks are built for the analyses that actually matter.
              </Workflow.Em>{' '}
              With an integrated toolkit, you no longer need to jump between
              tools to query, predict, transform, and visualize data.
            </SubHeadline>
          </HeadlineContainer>
          <HatchedContentMarginLine data-right />
        </HatchedCondensedGrid>

        <LayoutGridCondensed>
          <StyledContentMarginLine />
          <Bento>
            <PolyglotCell />
            <NoCodeCell />
            <AiCell />
            <MultiplayerCell />
          </Bento>
          <StyledContentMarginLine data-right />
        </LayoutGridCondensed>

        <HatchedCondensedGrid data-bottom>
          <HatchedContentMarginLine />
          <HatchedContentMarginLine data-right />
        </HatchedCondensedGrid>
      </LayoutSubgrid>
      <CheckeredMargin data-right />
      <CustomerStories />
    </Section>
  );
}
